import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Check, EyeOff, RotateCcw, ChevronDown, ArrowUpRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CareerCard } from "./CareerCard";
import { SEVERITY_META } from "@/lib/careerConfig";
import { cn } from "@/lib/utils";
import { translateFr as tfr } from "@/i18n";

export function BlockersCard({ blockers, playerId, onChanged }) {
  const [showTreated, setShowTreated] = useState(false);
  const active = blockers.filter((b) => b.state === "active");
  const treated = blockers.filter((b) => b.state !== "active");
  const LINKS = {
    contract_expiring: { to: "/contracts?tab=renewals", label: tfr("Voir les renouvellements") },
    no_active_discussion: { to: "/pipeline", label: tfr("Ouvrir le pipeline") },
    document_expiring: { to: `/players/${playerId}`, label: tfr("Voir les documents") },
  };

  const setState = async (key, state) => {
    try {
      await api.post(`/players/${playerId}/career-plan/blockers/${key}/state`, { state });
      toast.success(state === "resolved" ? tfr("Point de vigilance résolu") : state === "dismissed" ? tfr("Point de vigilance ignoré") : tfr("Point de vigilance réactivé"));
      onChanged?.();
    } catch { toast.error(tfr("Impossible de mettre à jour le point de vigilance")); }
  };

  return (
    <CareerCard
      icon={AlertTriangle}
      title={tfr("Points de vigilance")}
      subtitle={active.length ? `${active.length} ${active.length > 1 ? tfr("points à traiter") : tfr("point à traiter")}` : tfr("Rien à signaler")}
      testid="blockers-card"
    >
      {active.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4" data-testid="blockers-empty">
          {tfr("Aucun point de vigilance actif. 👍")}
        </p>
      ) : (
        <ul className="space-y-3">
          {active.map((b) => {
            const sev = SEVERITY_META[b.severity] || SEVERITY_META.low;
            return (
              <li key={b.key} className="rounded-xl border border-border bg-secondary/30 p-3.5" data-testid={`blocker-${b.key}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0", sev.cls)}>{sev.label}</Badge>
                      <p className="text-sm font-medium text-foreground/95">{b.title}</p>
                    </div>
                    {b.recommended_action && (
                      <p className="text-xs text-muted-foreground mt-1.5">→ {b.recommended_action}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 mt-2.5 flex-wrap">
                  <Button variant="outline" size="sm" onClick={() => setState(b.key, "resolved")}
                    className="h-7 gap-1 text-xs" data-testid={`blocker-resolve-${b.key}`}>
                    <Check className="h-3 w-3" /> Résolu
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setState(b.key, "dismissed")}
                    className="h-7 gap-1 text-xs text-muted-foreground" data-testid={`blocker-dismiss-${b.key}`}>
                    <EyeOff className="h-3 w-3" /> Ignorer
                  </Button>
                  {LINKS[b.key] && (
                    <Link to={LINKS[b.key].to} data-testid={`blocker-link-${b.key}`}
                      className="inline-flex items-center gap-1 text-xs text-athlynx hover:underline h-7 px-1">
                      <ArrowUpRight className="h-3 w-3" /> {LINKS[b.key].label}
                    </Link>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {treated.length > 0 && (
        <div className="mt-4 pt-3 border-t border-border">
          <button
            onClick={() => setShowTreated((v) => !v)}
            className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-athlynx/40 rounded"
            aria-expanded={showTreated}
            data-testid="blockers-treated-toggle"
          >
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", showTreated && "rotate-180")} aria-hidden="true" />
            Traités ({treated.length})
          </button>
          {showTreated && (
            <ul className="space-y-2 mt-2">
              {treated.map((b) => (
                <li key={b.key} className="flex items-center justify-between gap-2 text-sm text-muted-foreground" data-testid={`blocker-treated-${b.key}`}>
                  <span className="truncate">
                    {b.title} · <span className="text-[11px]">{b.state === "resolved" ? tfr("Résolu") : tfr("Ignoré")}</span>
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => setState(b.key, "active")}
                    className="h-6 gap-1 text-[11px] shrink-0" data-testid={`blocker-reopen-${b.key}`}>
                    <RotateCcw className="h-3 w-3" /> Réactiver
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <p className="text-[11px] text-muted-foreground mt-4 pt-3 border-t border-border">
        {tfr("Détectés automatiquement à partir des données réelles du dossier (contrat, documents, objectifs, pipeline).")}
      </p>
    </CareerCard>
  );
}
